import { useState } from "react";
import type { WabaTemplate } from "@school-sales-ai/shared";
import { BroadcastAuthorizationCard } from "@/components/broadcasts/BroadcastAuthorizationCard";
import { DeleteInMetaDialog } from "@/components/templates/DeleteInMetaDialog";
import { TemplateEditorDialog } from "@/components/templates/TemplateEditorDialog";
import { TemplatesTable, type TemplateRowBusy } from "@/components/templates/TemplatesTable";
import { useCurrentUserProfile } from "@/hooks/useUsers";
import {
  useArchiveWabaTemplate,
  useCreateWabaTemplate,
  useDeleteWabaTemplateInMeta,
  useSubmitWabaTemplate,
  useSyncWabaTemplates,
  useUnarchiveWabaTemplate,
  useUpdateWabaTemplate,
  useUploadTemplateMedia,
  useWabaTemplates,
} from "@/hooks/useWabaTemplates";

type EditorState = { open: boolean; template: WabaTemplate | null };

export function Plantillas(): React.JSX.Element {
  const profile = useCurrentUserProfile();
  const isSuperAdmin = profile?.role === "super_admin";
  const canEdit =
    profile?.role === "admin" ||
    profile?.role === "super_admin" ||
    profile?.role === "account_manager";

  const [includeArchived, setIncludeArchived] = useState(false);
  const [editor, setEditor] = useState<EditorState>({ open: false, template: null });
  const [deleting, setDeleting] = useState<WabaTemplate | null>(null);

  const { data: templates, isPending, isError, error } = useWabaTemplates(includeArchived);
  const uploadMedia = useUploadTemplateMedia();
  const create = useCreateWabaTemplate();
  const update = useUpdateWabaTemplate();
  const submit = useSubmitWabaTemplate();
  const archive = useArchiveWabaTemplate();
  const unarchive = useUnarchiveWabaTemplate();
  const sync = useSyncWabaTemplates();
  const deleteInMeta = useDeleteWabaTemplateInMeta();

  // Which row is waiting on the server. `variables` is the template id these
  // mutations were called with, so one shared hook still identifies its row.
  const busy: TemplateRowBusy = submit.isPending
    ? { id: submit.variables, action: "submit" }
    : archive.isPending
      ? { id: archive.variables, action: "archive" }
      : unarchive.isPending
        ? { id: unarchive.variables, action: "unarchive" }
        : null;

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink-900">Plantillas de WhatsApp</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-600">
            Para escribirle a alguien más de 24 horas después de su último mensaje,
            WhatsApp exige una plantilla aprobada por Meta. Aquí se crean, se envían a
            revisión y se ve en qué estado quedaron.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canEdit ? (
            <>
              <button
                type="button"
                onClick={() => sync.mutate(undefined)}
                disabled={sync.isPending}
                className="rounded-md border border-ink-200 px-3 py-2 text-sm text-ink-700 disabled:opacity-50"
              >
                {sync.isPending ? "Sincronizando…" : "Sincronizar con Meta"}
              </button>
              <button
                type="button"
                onClick={() => setEditor({ open: true, template: null })}
                className="rounded-md bg-ink-900 px-3 py-2 text-sm text-white"
              >
                Nueva plantilla
              </button>
            </>
          ) : null}
        </div>
      </div>

      <BroadcastAuthorizationCard />

      <label className="mt-4 flex items-center gap-2 text-sm text-ink-700">
        <input
          type="checkbox"
          checked={includeArchived}
          onChange={(e) => setIncludeArchived(e.target.checked)}
        />
        Mostrar archivadas
      </label>

      <div className="mt-4">
        {isPending ? (
          <p className="text-sm text-ink-500">Cargando…</p>
        ) : isError ? (
          <p className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {error.message}
          </p>
        ) : (
          <TemplatesTable
            templates={templates}
            isSuperAdmin={isSuperAdmin}
            canEdit={canEdit}
            busy={busy}
            onEdit={(t) => setEditor({ open: true, template: t })}
            onSubmit={(t) => submit.mutate(t.id)}
            onArchive={(t) => archive.mutate(t.id)}
            onUnarchive={(t) => unarchive.mutate(t.id)}
            onDeleteInMeta={(t) => setDeleting(t)}
          />
        )}
      </div>

      {editor.open ? (
        <TemplateEditorDialog
          template={editor.template}
          saving={create.isPending || update.isPending}
          uploadingMedia={uploadMedia.isPending}
          onUploadMedia={(file, kind, onDone) =>
            uploadMedia.mutate({ file, kind }, { onSuccess: onDone })
          }
          onClose={() => setEditor({ open: false, template: null })}
          onCreate={(input) =>
            create.mutate(input, {
              onSuccess: () => setEditor({ open: false, template: null }),
            })
          }
          onUpdate={(input) => {
            const id = editor.template?.id;
            if (!id) return;
            update.mutate(
              { id, input },
              { onSuccess: () => setEditor({ open: false, template: null }) },
            );
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteInMetaDialog
          template={deleting}
          pending={deleteInMeta.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() =>
            deleteInMeta.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
          }
        />
      ) : null}
    </div>
  );
}
